(ns machtfit.settlement-recipients.core
  (:require [clojure.string :as str]
            [machtfit.company-editor-styles :as styles]
            [machtfit.dashboard-widgets :as widgets]
            [machtfit.settlement-recipients.rules :as rules]
            [machtfit.utils :as utils]
            [machtfit.widgets.checkbox :as checkbox]
            [re-frame.core :as rf]))

(defn ^:export load [js-data]
  (rf/dispatch [::init (js->clj js-data :keywordize-keys true)]))

(rf/reg-event-db ::init
  (fn [db [_ data]]
    (let [data-recipients (-> data
                              :data
                              :recipients)
          recipients (mapv #(-> %1
                                (assoc :sort-key %2)
                                (update :delivery-method (fn [dms] (map keyword dms)))
                                (update :delivery-method set)) data-recipients (range))]
      (-> db
          (merge data)
          (assoc-in [:data :recipients] recipients)))))

(def ^:private th-numeric-classes (conj styles/th "text-right"))
(def ^:private td-numeric-classes (conj styles/td "text-right" "tabular-nums"))

(def ^:private text-input-classes
  ["w-full" "rounded-lg" "border" "border-gray-200" "bg-white" "px-2.5" "py-1.5" "text-sm" "text-gray-900"
   "transition-colors" "focus:border-brand-400" "focus:outline-none" "focus:ring" "focus:ring-brand-500/15"])

;; widgets/select-box takes no :class; style and width-cap edit-mode <select>s via descendant variants.
(def ^:private edit-control-variant-classes
  ["[&_select]:max-w-[240px]" "[&_select]:rounded-lg" "[&_select]:border" "[&_select]:border-gray-200" "[&_select]:bg-white"
   "[&_select]:px-2.5" "[&_select]:py-1.5" "[&_select]:text-sm" "[&_select]:text-gray-900"])

(rf/reg-sub ::num-recipients
  :<- [:get [:data :recipients]]

  (fn [recipients _]
    (count recipients)))

(rf/reg-sub ::num-employees
  :<- [:get [:data :employee-counts]]

  (fn [employee-counts _]
    (apply + (map :count employee-counts))))

(rf/reg-sub ::show-employee-count?
  :<- [::num-employees]
  :<- [::num-recipients]

  (fn [[num-employees num-recipients] _]
    (and (> num-employees 0)
         (> num-recipients 0))))

(rf/reg-sub ::show-subscription-count?
  :<- [::num-recipients]

  (fn [num-recipients _]
    (> num-recipients 0)))

(defn- recipients-header []
  [:thead
   [:tr
    [:th {:class styles/th} "Name"]
    [:th {:class styles/th} "Aktiv"]
    [:th {:class th-numeric-classes} "Kundennummer"]
    [:th {:class styles/th} "Adresse"]
    [:th {:class styles/th} "Referenz"]
    [:th {:class styles/th} "Zahlungsziel"]
    [:th {:class styles/th} "Vertriebsbereich"]
    [:th {:class styles/th} "Custify ID"]
    [:th {:class styles/th} "Versandart"]
    [:th {:class styles/th} "E-Mail-Adressen"]
    [:th {:class styles/th} "Notizen"]
    (when @(rf/subscribe [::show-employee-count?])
      [:th {:class th-numeric-classes} "Mitarbeiter"])
    (when @(rf/subscribe [::show-subscription-count?])
      [:th {:class th-numeric-classes} "Subscriptions"])
    [:th {:class styles/th}]]])

(rf/reg-sub ::rules-with-count-result
  :<- [:get [:data :rules]]
  :<- [:get [:data :employee-counts]]

  (fn [[rules employee-counts] _]
    (rules/add-counts rules employee-counts)))

(rf/reg-sub ::rules-with-count
  :<- [::rules-with-count-result]

  (fn [rules-with-count-result _]
    (:rules-with-count rules-with-count-result)))

(rf/reg-sub ::unruled-employee-count
  :<- [::rules-with-count-result]

  (fn [rules-with-count-result _]
    (:unruled-employees-count rules-with-count-result)))

(rf/reg-sub ::recipient-employee-count
  :<- [::rules-with-count]

  (fn [rules-with-count [_ id]]
    (apply + (map :count (filter #(= id (:result %)) rules-with-count)))))

(defn- employee-count [c]
  (if (= 0 c)
    [:span {:class ["text-gray-300"]} c]
    c))

(defn- recipient-employee-count [id]
  [:td {:class td-numeric-classes} (employee-count @(rf/subscribe [::recipient-employee-count id]))])

(rf/reg-sub ::recipient-by-id
  :<- [:get [:data :recipients]]

  (fn [recipients [_ id]]
    (utils/by-id recipients id)))

(def ^:private delivery-methods
  {:email-attachment "E-Mail-Anhang"
   :email-downloadlink "E-Mail-Downloadlink"
   :postal-delivery "Postalische Zustellung"})

(rf/reg-event-db ::edit-recipient
  (fn [db [_ index]]
    (assoc-in db [:ui :editing] {:recipient index})))

(defn- recipient [id]
  (let [{:keys [name
                customer-number
                address
                reference
                payment-period-days
                sales-area
                delivery-method
                delivery-notes
                accounting-information
                num-subscriptions
                custify-company-id
                active]}
        @(rf/subscribe [::recipient-by-id id])]
    [:tr {:class ["border-t" "border-gray-100"]}
     [:td {:class (conj styles/td "font-medium" "text-gray-900")} name]
     [:td {:class styles/td} [checkbox/checkbox {:checked? active}]]
     [:td {:class td-numeric-classes} customer-number]
     [:td {:class (conj styles/td "whitespace-pre-line")} address]
     [:td {:class styles/td} reference]
     [:td {:class styles/td} payment-period-days (if (= 1 payment-period-days) " Tag" " Tage")]
     [:td {:class styles/td} sales-area]
     [:td {:class styles/td} custify-company-id]
     [:td {:class styles/td}
      (into [:ul {:class ["flex" "flex-col" "gap-0.5"]}]
            (for [method delivery-method]
              ^{:key method} [:li (delivery-methods method)]))]
     [:td {:class (conj styles/td "max-w-xs" "whitespace-pre-line" "break-words")} delivery-notes]
     [:td {:class (conj styles/td "whitespace-pre-line")} accounting-information]
     (when @(rf/subscribe [::show-employee-count?])
       [recipient-employee-count id])
     (when @(rf/subscribe [::show-subscription-count?])
       [:td {:class td-numeric-classes} (employee-count num-subscriptions)])
     [:td {:class (conj styles/td "text-right")}
      [:button {:type "button"
                :class styles/edit-action
                :on-click #(rf/dispatch [::edit-recipient id])} "bearbeiten"]]]))

(rf/reg-sub ::sorted-recipient-ids
  :<- [:get [:data :recipients]]

  (fn [recipients _]
    (->> recipients
         (sort-by :sort-key)
         (map :id))))

(rf/reg-sub ::recipient-table-num-cols
  :<- [::show-employee-count?]
  :<- [::show-subscription-count?]

  (fn [[show-employee-count? show-subscription-count?] _]
    (cond-> 12
      show-employee-count? inc
      show-subscription-count? inc)))

(rf/reg-sub ::recipients-total-employee-count
  :<- [::rules-with-count]
  :<- [:get [:data :recipients]]

  (fn [[rules-with-count recipients] _]
    (let [recipient-ids (map :id recipients)]
      (apply + (map :count (filter #(some #{(:result %)} recipient-ids) rules-with-count))))))

(defn- recipient-sum []
  [:tr {:class ["border-t" "border-gray-200" "bg-gray-25" "font-semibold" "text-gray-900"]}
   [:td {:col-span 11 :class ["px-4" "py-3"]}]
   (when @(rf/subscribe [::show-employee-count?])
     [:td {:class ["px-4" "py-3" "text-right" "tabular-nums"]}
      @(rf/subscribe [::recipients-total-employee-count])])
   (when @(rf/subscribe [::show-subscription-count?])
     [:td {:class ["px-4" "py-3"]}])
   [:td {:class ["px-4" "py-3"]}]])

(defn- id->index [coll id]
  (let [index (count (take-while #(not= id (:id %)) coll))]
    (if (= index (count coll))
      nil index)))

(rf/reg-event-db ::set-recipient-attr
  (fn [db [_ id attr name]]
    (let [index (id->index (-> db :data :recipients) id)]
      (assoc-in db [:data :recipients index attr] name))))

(defn- edit-recipient [id]
  (let [{:keys [name
                customer-number
                address
                reference
                payment-period-days
                sales-area
                delivery-method
                delivery-notes
                accounting-information
                num-subscriptions
                custify-company-id
                active]}
        @(rf/subscribe [::recipient-by-id id])]
    [:tr {:class (into ["border-t" "border-gray-100"] edit-control-variant-classes)}
     [:td {:class styles/td} [widgets/input {:value name
                                             :placeholder "Name"
                                             :class text-input-classes
                                             :on-save #(rf/dispatch [::set-recipient-attr id :name %])}]]
     [:td {:class styles/td} [checkbox/checkbox {:checked? active
                                                 :on-click #(rf/dispatch [::set-recipient-attr id :active (not active)])}]]
     [:td {:class td-numeric-classes} customer-number]
     [:td {:class styles/td} [widgets/textarea {:value address
                                                :placeholder "Adresse"
                                                :class (conj text-input-classes "min-h-20")
                                                :on-save #(rf/dispatch [::set-recipient-attr id :address %])}]]
     [:td {:class styles/td} [widgets/input {:value reference
                                             :placeholder "Referenz"
                                             :class text-input-classes
                                             :on-save #(rf/dispatch [::set-recipient-attr id :reference %])}]]
     [:td {:class styles/td}
      [widgets/input {:value payment-period-days
                      :placeholder "Zahlungsziel"
                      :type widgets/type-positive-number
                      :class text-input-classes
                      :on-save #(rf/dispatch [::set-recipient-attr id :payment-period-days %])}]
      (if (= 1 payment-period-days) " Tag" " Tage")]
     [:td {:class styles/td} [widgets/input {:value sales-area
                                             :placeholder "Vertriebsbereich"
                                             :class text-input-classes
                                             :on-save #(rf/dispatch [::set-recipient-attr id :sales-area %])}]]
     [:td {:class styles/td} [widgets/input {:value custify-company-id
                                             :placeholder "Custify Company ID"
                                             :type widgets/type-positive-number-or-nil
                                             :class text-input-classes
                                             :on-save #(rf/dispatch [::set-recipient-attr id :custify-company-id %])}]]
     [:td {:class styles/td} [widgets/multi-select {:name "delivery-method"
                                                    :value delivery-method
                                                    :choices delivery-methods
                                                    :on-save #(rf/dispatch [::set-recipient-attr id :delivery-method %])}]]
     [:td {:class styles/td} [widgets/textarea {:value delivery-notes
                                                :placeholder "E-Mail-Adressen"
                                                :class (conj text-input-classes "min-h-20")
                                                :on-save #(rf/dispatch [::set-recipient-attr id :delivery-notes %])}]]
     [:td {:class styles/td} [widgets/textarea {:value accounting-information
                                                :placeholder "Notizen"
                                                :class (conj text-input-classes "min-h-20")
                                                :on-save #(rf/dispatch [::set-recipient-attr id :accounting-information %])}]]
     (when @(rf/subscribe [::show-employee-count?])
       [recipient-employee-count id])
     (when @(rf/subscribe [::show-subscription-count?])
       [:td {:class td-numeric-classes} (employee-count num-subscriptions)])
     [:td {:class (conj styles/td "text-right")}
      [:button {:type "button"
                :class styles/done-action
                :on-click #(rf/dispatch [::edit-recipient nil])} "fertig"]]]))

(defn- recipients-body []
  (let [ui-edit-recipient @(rf/subscribe [:get [:ui :editing :recipient]])]
    [:tbody
     (for [id @(rf/subscribe [::sorted-recipient-ids])]
       (if (= ui-edit-recipient id)
         ^{:key id} [edit-recipient id]
         ^{:key id} [recipient id]))
     [recipient-sum]]))

(rf/reg-event-db ::add-recipient
  (fn [db _]
    (let [recipients (-> db :data :recipients)
          max-sort-key (->> recipients (map :sort-key) (apply max))
          max-id (->> recipients (map :id) (apply max))
          next-sort-key (if (nil? max-sort-key) 0 (inc max-sort-key))
          next-id (if (nil? max-id) 0 (inc max-id))
          new-recipient {:customer-number "neu"
                         :sort-key next-sort-key
                         :id next-id
                         :payment-period-days 14
                         :num-subscriptions 0
                         :active true}]
      (-> db
          (update-in [:data :recipients] conj new-recipient)
          (assoc-in [:ui :editing] {:recipient next-id})))))

(defn- recipients-add []
  [:tfoot
   [:tr
    [:td {:col-span @(rf/subscribe [::recipient-table-num-cols]) :class ["border-t" "border-gray-100" "px-4" "py-4"]}
     [:button {:type "button"
               :class styles/ghost-button
               :on-click #(rf/dispatch [::add-recipient])}
      "+ Abrechnungsempfänger hinzufügen"]]]])

(defn- recipients []
  [:div {:class (conj styles/card "w-full")}
   [:div {:class ["border-b" "border-gray-100" "px-6" "py-4"]}
    [:h3 {:class ["text-base" "font-semibold" "text-gray-900"]} "Abrechnungsempfänger"]]
   [:div {:class ["overflow-x-auto"]}
    [:table {:class ["w-full" "text-left" "text-sm"]}
     [recipients-header]
     [recipients-body]
     [recipients-add]]]])

(rf/reg-sub ::rule-by-index
  :<- [::rules-with-count]

  (fn [rules-with-count [_ index]]
    (get rules-with-count index)))

(rf/reg-event-db ::move-rule-up
  (fn [db [_ index]]
    (-> db
        (update-in [:data :rules] utils/move-up index)
        (update-in [:ui :editing :rule] utils/decrease-index))))

(rf/reg-event-db ::move-rule-down
  (fn [db [_ index]]
    (-> db
        (update-in [:data :rules] utils/move-down index)
        (update-in [:ui :editing :rule] utils/increase-index))))

(rf/reg-sub ::num-rules
  :<- [:get [:data :rules]]

  (fn [rules _]
    (count rules)))

(rf/reg-event-db ::set-rule-result
  (fn [db [_ index result]]
    (assoc-in db [:data :rules index :result] result)))

(rf/reg-event-db ::edit-rule
  (fn [db [_ index]]
    (assoc-in db [:ui :editing] {:rule index})))

(rf/reg-sub ::places-of-business-by-id
  :<- [:get [:data :condition-options :places-of-business]]

  (fn [places-of-business [_ id]]
    (utils/by-id places-of-business id)))

(rf/reg-sub ::employee-group-by-id
  :<- [:get [:data :condition-options :employee-groups]]

  (fn [employee-groups [_ id]]
    (utils/by-id employee-groups id)))

(defn- rule-conditions [c]
  (let [num-rules @(rf/subscribe [::num-rules])]
    [:span
     (when (:subsidiary c)
       (str " Unternehmensteil: " (:subsidiary c)))
     (when (:occupation-group c)
       (str " Berufsgruppe: " (:occupation-group c)))
     (when (:place-of-business c)
       (str " Standort: " (:name @(rf/subscribe [::places-of-business-by-id (:place-of-business c)]))))
     (when (:employee-group c)
       (str " Mitarbeitergruppe: " (:name @(rf/subscribe [::employee-group-by-id (:employee-group c)]))))
     (when (not (some some? (vals c)))
       (if (= 1 num-rules)
         "Alle Mitarbeiter"
         "restliche Mitarbeiter"))]))

(rf/reg-sub ::occupation-group-names-used-in-rules
  :<- [:get [:data :rules]]

  (fn [rules _]
    (keep #(-> % :condition :occupation-group) rules)))

(rf/reg-sub ::occupation-group-names-from-backend
  :<- [:get [:data :condition-options :occupation-groups]]

  (fn [groups _]
    (map :name groups)))

(def default-new-name "")

(def default-value-string "–––")

(def string-representing-nil "<no-value>")

(rf/reg-sub ::new-occupation-groups
  :<- [::occupation-group-names-used-in-rules]
  :<- [::occupation-group-names-from-backend]

  (fn [[occupation-group-names-used-in-rules occupation-group-names-from-backend] _]
    (let [backend-names-set (set occupation-group-names-from-backend)]
      (set (remove #(or (contains? backend-names-set %)
                        (= % default-new-name)
                        (= % string-representing-nil))
                   occupation-group-names-used-in-rules)))))

(defn- edit-rule-group [index]
  (let [rule @(rf/subscribe [::rule-by-index index])
        current-name (-> rule :condition :occupation-group)]

    [:<> [:tr
          [:td "Berufsgruppe:"]
          [:td [widgets/select-box
                {:value (or current-name
                            string-representing-nil)
                 :on-change #(do
                               (rf/dispatch [:set-path [:data :rules index :condition :occupation-group]
                                             (when (not= string-representing-nil %) %)]))
                 :data (concat [{:value string-representing-nil
                                 :label default-value-string}
                                {:value default-new-name
                                 :label "<neu anlegen>"}]
                               (map (fn [new-group]
                                      {:value new-group
                                       :label (str new-group " (neu)")})
                                    @(rf/subscribe [::new-occupation-groups]))
                               (map (fn [{:keys [id name]}]
                                      {:value id
                                       :label name})
                                    @(rf/subscribe [:get [:data :condition-options :occupation-groups]])))}]]]
     (when (= current-name default-new-name)
       [:tr
        [:td "Neue Berufsgruppe"]
        [:td
         [widgets/input {:autofocus? true
                         :on-save #(rf/dispatch [:set-path
                                                 [:data :rules index :condition :occupation-group]
                                                 (when %
                                                   (str/trim %))])}]]])]))

(rf/reg-sub ::subsidiary-names-used-in-rules
  :<- [:get [:data :rules]]

  (fn [rules _]
    (keep #(-> % :condition :subsidiary) rules)))

(rf/reg-sub ::subsidiary-names-from-backend
  :<- [:get [:data :condition-options :subsidiaries]]

  (fn [groups _]
    (map :name groups)))

(rf/reg-sub ::new-subsidiaries
  :<- [::subsidiary-names-used-in-rules]
  :<- [::subsidiary-names-from-backend]

  (fn [[subsidiary-names-used-in-rules subsidiary-names-from-backend] _]
    (let [backend-names-set (set subsidiary-names-from-backend)]
      (set (remove #(or (contains? backend-names-set %)
                        (= % default-new-name)
                        (= % string-representing-nil))
                   subsidiary-names-used-in-rules)))))

(defn- edit-rule-subsidiary [index]
  (let [rule @(rf/subscribe [::rule-by-index index])
        current-name (-> rule :condition :subsidiary)]
    [:<> [:tr
          [:td "Unternehmensteil:"]
          [:td [widgets/select-box
                {:value (or current-name
                            string-representing-nil)
                 :on-change #(do
                               (rf/dispatch [:set-path [:data :rules index :condition :subsidiary]
                                             (case %
                                               string-representing-nil nil
                                               %)]))
                 :data (concat [{:value string-representing-nil
                                 :label default-value-string}
                                {:value default-new-name
                                 :label "<neu anlegen>"}]
                               (map (fn [new-subsidiary]
                                      {:value new-subsidiary
                                       :label (str new-subsidiary " (neu)")})
                                    @(rf/subscribe [::new-subsidiaries]))
                               (map (fn [{:keys [id name]}]
                                      {:value id
                                       :label name})
                                    @(rf/subscribe [:get [:data :condition-options :subsidiaries]])))}]]]
     (when (= current-name default-new-name)
       [:tr
        [:td "Neuer Unternehmensteil"]
        [:td
         [widgets/input {:autofocus? true
                         :on-save #(rf/dispatch [:set-path
                                                 [:data :rules index :condition :subsidiary]
                                                 (when %
                                                   (str/trim %))])}]]])]))

(defn- edit-rule-place-of-business [index]
  (let [rule @(rf/subscribe [::rule-by-index index])]
    [:tr
     [:td "Standort:"]
     [:td [widgets/select-box
           {:value (or (-> rule :condition :place-of-business) string-representing-nil)
            :on-change #(rf/dispatch [:set-path [:data :rules index :condition :place-of-business] (if (= string-representing-nil %) nil (int %))])
            :data (into [{:value string-representing-nil :label default-value-string}]
                        (map #(identity {:value (:id %) :label (:name %)})
                             @(rf/subscribe [:get [:data :condition-options :places-of-business]])))}]]]))

(defn- edit-rule-employee-group [index]
  (let [rule @(rf/subscribe [::rule-by-index index])
        condition-path [:data :rules index :condition :employee-group]]
    [:tr
     [:td "Mitarbeitergruppe:"]
     [:td [widgets/select-box
           {:value (or (-> rule :condition :employee-group) default-value-string)
            :on-change #(rf/dispatch [:set-path condition-path (if (= default-value-string %) nil (int %))])
            :data (into [{:value default-value-string :label default-value-string}]
                        (map #(identity {:value (:id %) :label (:name %)})
                             @(rf/subscribe [:get [:data :condition-options :employee-groups]])))}]]]))

(defn- edit-rule [index]
  [:table>tbody
   [edit-rule-group index]
   [edit-rule-place-of-business index]
   [edit-rule-subsidiary index]
   [edit-rule-employee-group index]])

(rf/reg-event-db ::delete-rule
  (fn [db [_ index]]
    (update-in db [:data :rules] utils/delete-at index)))

(defn- rule [index]
  (let [rule' @(rf/subscribe [::rule-by-index index])
        num-rules @(rf/subscribe [::num-rules])
        editing? (= index @(rf/subscribe [:get [:ui :editing :rule]]))]
    [:tr {:class (into ["border-t" "border-gray-100"] edit-control-variant-classes)}
     [:td {:class (conj styles/td "w-px")}
      [:div {:class ["flex" "items-center" "gap-1"]}
       (when (< (inc index) num-rules)
         [:button {:type "button"
                   :class styles/reorder-button
                   :aria-label "Regel nach unten verschieben"
                   :on-click #(rf/dispatch [::move-rule-down index])}
          [styles/arrow-icon true]])
       (when-not (= 0 index)
         [:button {:type "button"
                   :class styles/reorder-button
                   :aria-label "Regel nach oben verschieben"
                   :on-click #(rf/dispatch [::move-rule-up index])}
          [styles/arrow-icon false]])]]

     [:td {:class (conj styles/td "text-gray-900")}
      (if editing?
        [edit-rule index]
        [rule-conditions (:condition rule')])]

     [:td {:class styles/td}
      [widgets/select-box
       {:value (:result rule')
        :on-change #(rf/dispatch [::set-rule-result index (int %)])
        :data (mapv (fn [{:keys [id name customer-number active]}]
                      {:value id
                       :label (str name " (" customer-number ")" (when-not active " (inaktiv)"))})
                    @(rf/subscribe [:get [:data :recipients]]))}]]

     [:td {:class td-numeric-classes} (employee-count (:count rule'))]

     [:td {:class (conj styles/td "whitespace-nowrap" "text-right")}
      [:div {:class ["flex" "items-center" "justify-end" "gap-2"]}
       (when-not editing?
         [:button {:type "button"
                   :class styles/edit-action
                   :on-click #(rf/dispatch [::edit-rule index])} "bearbeiten"])
       (when editing?
         [:button {:type "button"
                   :class styles/done-action
                   :on-click #(rf/dispatch [::edit-rule nil])} "fertig"])
       [:button {:type "button"
                 :class styles/delete-action
                 :on-click #(rf/dispatch [::delete-rule index])} "löschen"]]]]))

(rf/reg-event-db ::add-rule
  (fn [db _]
    (let [first-recipient (-> db :data :recipients first)
          new-rule {:result (:id first-recipient)}
          add-rule #(conj (if (empty? %) [] %) new-rule)]
      (-> db
          (update-in [:data :rules] add-rule)
          (assoc-in [:ui :editing] {:rule (count (-> db :data :rules))})))))

(rf/reg-sub ::has-recipients?
  :<- [:get [:data :recipients]]

  (fn [recipients' _]
    (empty? recipients')))

(defn- rules []
  (when-not @(rf/subscribe [::has-recipients?])
    [:div {:class (conj styles/card "w-full")}
     [:div {:class ["border-b" "border-gray-100" "px-6" "py-4"]}
      [:h3 {:class ["text-base" "font-semibold" "text-gray-900"]}
       "Zuordnung von Buchungsrechnungen (Mitarbeitern) zu Abrechnungsempfängern"]]
     [:div {:class ["overflow-x-auto"]}
      [:table {:class ["w-full" "text-left" "text-sm"]}
       [:thead
        [:tr
         [:th {:class styles/th}]
         [:th {:class styles/th} "Falls"]
         [:th {:class styles/th} "Dann"]
         [:th {:class th-numeric-classes} "Mitarbeiter"]
         [:th {:class styles/th}]]]

       (into [:tbody]
             (for [index (range @(rf/subscribe [::num-rules]))]
               ^{:key index}
               [rule index]))
       [:tfoot
        [:tr {:class ["border-t" "border-gray-200" "bg-gray-25" "font-semibold" "text-gray-900"]}
         [:th {:col-span 3 :class ["px-4" "py-3" "text-right"]} "Nicht zugeordnete Mitarbeiter"]
         [:td {:class ["px-4" "py-3" "text-right" "tabular-nums"]} (employee-count @(rf/subscribe [::unruled-employee-count]))]
         [:td {:class ["px-4" "py-3"]}]]
        [:tr
         [:td {:col-span 5 :class ["border-t" "border-gray-100" "px-4" "py-4"]}
          [:button {:type "button"
                    :class styles/ghost-button
                    :on-click #(rf/dispatch [::add-rule])}
           "+ Regel hinzufügen"]]]]]]]))

(rf/reg-event-fx ::save-rules-recipients
  (fn [{:keys [db]} _]
    {:post {:url (get-in db [:io :save-url])
            :csrf-token (get-in db [:io :csrf-token])
            :data (clj->js {:recipients (-> db :data :recipients)
                            :rules (-> db :data :rules)})
            :on-success #(rf/dispatch [::save-rules-recipients-success %])
            :on-error #(rf/dispatch [::save-rules-recipients-error])}}))

(rf/reg-event-fx ::save-rules-recipients-success
  (fn [_ [_ result]]
    {:redirect {:url (:redirect-url result)}}))

(rf/reg-event-fx ::save-rules-recipients-error
  (fn [_ _]
    {:alert {:msg "Das hat leider nicht geklappt. Bitte wende dich an das Produkt-Team."}}))

(defn- save-button []
  [:button {:type "button"
            :class styles/primary-button
            :on-click #(rf/dispatch [::save-rules-recipients])} "Speichern"])

(defn company []
  [:div#settlement-recipients
   #_[:button {:on-click #(doall (map rf/dispatch-sync [[::add-recipient]
                                                        [::set-recipient-attr 0 :name "Wärme AG"]
                                                        [::set-recipient-attr 0 :address "Temperaturstrasse 1\n21086 Celsius"]
                                                        [::set-recipient-attr 0 :reference "SAP-Nr. 2"]
                                                        [::set-recipient-attr 0 :payment-period-days 14]
                                                        [::set-recipient-attr 0 :sales-area "Sales"]
                                                        [::set-recipient-attr 0 :delivery-method '(:email-downloadlink :email-attachment)]
                                                        [::set-recipient-attr 0 :delivery-notes "Normaler Versand\nim goldenen Umschlag"]
                                                        [::add-recipient]
                                                        [::set-recipient-attr 1 :name "Kälte UG"]
                                                        [::set-recipient-attr 1 :address "Temperaturstr. 2\n21087 Fahrenheit"]
                                                        [::set-recipient-attr 1 :reference "SAP-Nr. 486946"]
                                                        [::set-recipient-attr 1 :payment-period-days 30]
                                                        [::set-recipient-attr 1 :sales-area "Money Laundering"]
                                                        [::set-recipient-attr 1 :delivery-method '(:postal-delivery)]
                                                        [::set-recipient-attr 1 :delivery-notes "Umschlag nicht mit Spucke befeuchten!"]
                                                        [::edit-recipient nil]]))}
      "Auto"]
   [:div {:class ["flex" "flex-col" "items-start" "gap-5"]}
    [recipients]
    [rules]
    [save-button]]])
